/**
 * aiTools.js — the /ai-lab/tools directory.
 *
 * One entry per tool. `recommended: true` marks the tool the platform tells users to pick
 * for that group (we show ONE Recommended badge per group rather than ten equal choices).
 * Logos: components render https://www.google.com/s2/favicons?domain=<domain>&sz=128 with an
 * initial-letter fallback, so no image assets are needed.
 *
 * Pricing is intentionally coarse ("Free tier", "Free + paid", "Paid") — exact prices change often.
 */
export const TOOL_GROUPS = [
  { id: "text", name: "Text & Strategy", blurb: "Writing, planning, analysis and every prompt from the Prompt Builder." },
  { id: "image", name: "Design & Image", blurb: "Packaging concepts, logos, product renders, mood boards and ad visuals." },
  { id: "video", name: "Video", blurb: "Product animation, AI presenters, UGC-style clips and launch videos." },
  { id: "audio", name: "Voice & Audio", blurb: "Voiceovers, dubbing, podcast clean-up and music beds." },
  { id: "production", name: "Design Production", blurb: "Turning AI concepts into print-ready, vector, production files." },
  { id: "website", name: "Websites & E-commerce", blurb: "Building the storefront and landing pages the AI wrote copy for." },
  { id: "presentation", name: "Presentations & Sell Sheets", blurb: "Buyer decks, investor decks and one-pagers from AI-written content." },
  { id: "research", name: "Research & Data", blurb: "Sourced market research, competitor scans and working spreadsheets." },
];

export const AI_TOOLS = [
  // ── TEXT & STRATEGY ────────────────────────────────────────────────────────
  {
    id: "chatgpt",
    name: "ChatGPT",
    group: "text",
    domain: "chatgpt.com",
    url: "https://chatgpt.com/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "The default place to paste any Prompt Builder prompt.",
    why: "Best all-rounder: strong writing, follows long structured prompts well, can also generate and edit images in the same chat, and can read your uploaded PDFs, spreadsheets and photos.",
    bestFor: ["Any Prompt Builder prompt", "Image generation & edits", "Analyzing uploaded files", "Iterating on drafts"],
    tip: "Start a new chat per project and keep iterating in the same thread so it remembers your brand context.",
  },
  {
    id: "claude",
    name: "Claude",
    group: "text",
    domain: "claude.ai",
    url: "https://claude.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Best for long, polished writing and careful analysis.",
    why: "Excels at long-form copy (website, investor materials, sell-sheet narratives), nuanced brand voice, and working through big documents or spreadsheets without losing the thread.",
    bestFor: ["Website & founder story copy", "Investor summaries", "Reviewing long documents", "Brand voice consistency"],
    tip: "Create a Project and paste your Brand Profile into its instructions — every chat inside it will stay on-brand.",
  },
  {
    id: "gemini",
    name: "Gemini",
    group: "text",
    domain: "gemini.google.com",
    url: "https://gemini.google.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Great with Google Workspace, video and fast image generation.",
    why: "Deep Research mode produces sourced market reports, it connects to Docs/Sheets/Drive, and its image and video models (Nano Banana, Veo) live in the same app.",
    bestFor: ["Deep Research reports", "Google Sheets & Docs users", "Quick image concepts", "Veo video generation"],
    tip: "Use Deep Research for the Research AI prompts — it will browse dozens of sources and cite them.",
  },
  {
    id: "perplexity",
    name: "Perplexity",
    group: "text",
    domain: "perplexity.ai",
    url: "https://www.perplexity.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Answers with live, cited sources — ideal for research prompts.",
    why: "Searches the web in real time and cites every claim, so competitor facts, pricing, retailer news and trend data are current and checkable.",
    bestFor: ["Competitor research", "Retailer & buyer research", "Pricing comparisons", "Trend scans"],
    tip: "Switch to Pro/Deep Research mode for the Market Analysis and Category Opportunity prompts.",
  },
  {
    id: "notebooklm",
    name: "NotebookLM",
    group: "text",
    domain: "notebooklm.google.com",
    url: "https://notebooklm.google.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Upload your own documents and ask questions across them.",
    why: "Grounds every answer in the files you upload (category reports, retailer guidelines, your own data) and can turn them into summaries, FAQs and audio overviews.",
    bestFor: ["Summarizing category reports", "Retailer vendor guidelines", "Building FAQs from your docs"],
    tip: "Upload your distributor price lists and ask it to build a margin table for you.",
  },

  // ── DESIGN & IMAGE ─────────────────────────────────────────────────────────
  {
    id: "chatgpt-images",
    name: "ChatGPT Image Generation",
    group: "image",
    domain: "chatgpt.com",
    url: "https://chatgpt.com/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "Best starting point for packaging concepts, mockups and ad visuals.",
    why: "Renders legible text on packaging, follows detailed layout instructions, and lets you upload your own can/bag/logo photo and say “put this on a shelf” or “change the flavor to mango.”",
    bestFor: ["Packaging concepts with real text", "Product renders from a photo", "Shelf & lifestyle mockups", "Static ad creative"],
    tip: "Upload your actual logo or packaging photo first, then paste the prompt — results stay on-brand.",
  },
  {
    id: "midjourney",
    name: "Midjourney",
    group: "image",
    domain: "midjourney.com",
    url: "https://www.midjourney.com/",
    recommended: false,
    pricing: "Paid",
    tagline: "The most beautiful mood boards and photography-grade imagery.",
    why: "Unmatched aesthetic quality for mood boards, lifestyle scenes and hero imagery. Weaker at exact text and strict layouts — use it for direction, not final artwork.",
    bestFor: ["Mood boards", "Lifestyle & hero photography", "Visual direction exploration", "Texture & ingredient imagery"],
    tip: "Add --ar 4:5 for Instagram, --ar 16:9 for presentation slides, and --style raw for realistic photography.",
  },
  {
    id: "firefly",
    name: "Adobe Firefly",
    group: "image",
    domain: "adobe.com",
    url: "https://firefly.adobe.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Commercially safe images that flow straight into Photoshop & Illustrator.",
    why: "Trained on licensed content (safer for commercial use), and Generative Fill / Expand inside Photoshop is the fastest way to extend backgrounds and clean up product shots.",
    bestFor: ["Commercially safe imagery", "Background extension", "Product retouching", "Adobe users"],
    tip: "Use Generative Expand in Photoshop to turn a square product shot into a 16:9 banner.",
  },
  {
    id: "ideogram",
    name: "Ideogram",
    group: "image",
    domain: "ideogram.ai",
    url: "https://ideogram.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Strongest at typography, logos and text-heavy designs.",
    why: "Renders words accurately, which makes it the best pick for logo explorations, wordmarks, posters and label layouts where the brand name has to be readable.",
    bestFor: ["Logo & wordmark concepts", "Typography exploration", "Posters & label layouts"],
    tip: "Put the exact brand name in quotes in the prompt so it spells it correctly.",
  },
  {
    id: "recraft",
    name: "Recraft",
    group: "image",
    domain: "recraft.ai",
    url: "https://www.recraft.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Generates true vector (SVG) logos and icons you can edit.",
    why: "One of the few tools that outputs real vector files, so an AI logo concept can be opened and refined in Illustrator or Figma instead of redrawn.",
    bestFor: ["Vector logos & icons", "Illustration systems", "Brand style consistency"],
    tip: "Create a custom Style from your best concept so every icon and illustration matches.",
  },
  {
    id: "canva",
    name: "Canva",
    group: "image",
    domain: "canva.com",
    url: "https://www.canva.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Fastest way to lay out social posts, sell sheets and simple ads.",
    why: "Templates plus built-in AI (Magic Design, Magic Write, background remover, resize). Perfect for non-designers who need finished, sized assets quickly.",
    bestFor: ["Social posts", "Sell sheets & one-pagers", "Quick ad layouts", "Resizing for every platform"],
    tip: "Set up a Brand Kit with your colors, fonts and logo once — every template will use them.",
  },

  // ── VIDEO ──────────────────────────────────────────────────────────────────
  {
    id: "runway",
    name: "Runway",
    group: "video",
    domain: "runwayml.com",
    url: "https://runwayml.com/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "Turn a product image into a cinematic product animation.",
    why: "Image-to-video with precise camera controls, plus an editor for trimming and upscaling — the most complete toolkit for short product and launch clips.",
    bestFor: ["Product animation from a render", "Launch teasers", "Cinematic b-roll", "Image-to-video"],
    tip: "Start from a clean product render with a simple background; add motion direction like “slow 360° turntable, soft studio light.”",
  },
  {
    id: "kling",
    name: "Kling",
    group: "video",
    domain: "klingai.com",
    url: "https://klingai.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Realistic motion and people — strong for UGC-style clips.",
    why: "Very natural human motion and physics (pouring, splashing, hands holding products), and start/end-frame control for controlled transitions.",
    bestFor: ["UGC-style product clips", "Pour & splash shots", "Hands-with-product scenes"],
    tip: "Use start-frame + end-frame to animate from a closed package to an open one.",
  },
  {
    id: "veo",
    name: "Google Veo (via Gemini / Flow)",
    group: "video",
    domain: "labs.google",
    url: "https://labs.google/flow",
    recommended: false,
    pricing: "Paid (included in Google AI plans)",
    tagline: "High-fidelity video with native sound effects and dialogue.",
    why: "Generates video with synchronized audio (ambient sound, fizz, dialogue), and Flow lets you build multi-shot scenes with consistent characters and products.",
    bestFor: ["Clips with sound", "Multi-shot sequences", "Lifestyle storytelling"],
    tip: "Describe the sound you want in the prompt (“crisp can-crack, ice clinking”).",
  },
  {
    id: "sora",
    name: "Sora",
    group: "video",
    domain: "sora.com",
    url: "https://sora.chatgpt.com/",
    recommended: false,
    pricing: "Included with ChatGPT paid plans",
    tagline: "Storyboard-driven video generation inside the OpenAI ecosystem.",
    why: "Built-in storyboard tool lets you place beats on a timeline, remix existing clips, and reuse your ChatGPT prompts and images.",
    bestFor: ["Storyboarded concepts", "Remixing clips", "ChatGPT users"],
    tip: "Generate your storyboard frames in ChatGPT first, then animate them in Sora for consistency.",
  },
  {
    id: "heygen",
    name: "HeyGen",
    group: "video",
    domain: "heygen.com",
    url: "https://www.heygen.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "AI presenters and avatar spokespeople from a script.",
    why: "Paste a script and get a realistic presenter on camera — including a digital twin of the founder — with translation into other languages and lip-sync.",
    bestFor: ["Founder / presenter videos", "Buyer video pitches", "Product explainers", "Multi-language versions"],
    tip: "Record a 2-minute video of yourself once to create your own avatar, then generate unlimited founder updates.",
  },
  {
    id: "higgsfield",
    name: "Higgsfield",
    group: "video",
    domain: "higgsfield.ai",
    url: "https://higgsfield.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Preset camera moves and effects for scroll-stopping ads.",
    why: "Library of cinematic camera presets (crash zoom, bullet time, dolly) that make product clips look like high-budget ads without prompting skill.",
    bestFor: ["Short-form ad clips", "Dramatic camera moves", "TikTok / Reels hooks"],
    tip: "Pick a preset first, then upload your product image — it does the rest.",
  },
  {
    id: "capcut",
    name: "CapCut",
    group: "video",
    domain: "capcut.com",
    url: "https://www.capcut.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Edit, caption and resize your AI clips into finished posts.",
    why: "Auto-captions, trending templates, background removal and one-click resize to 9:16 / 1:1 / 16:9 — the finishing step for everything the video tools generate.",
    bestFor: ["Editing & captions", "Resizing to every platform", "Adding music & text overlays"],
    tip: "Turn auto-captions on for every short-form video — most viewers watch muted.",
  },

  // ── VOICE & AUDIO ──────────────────────────────────────────────────────────
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    group: "audio",
    domain: "elevenlabs.io",
    url: "https://elevenlabs.io/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "Studio-quality voiceovers from your script in minutes.",
    why: "The most natural AI voices, with voice cloning, multiple languages, sound effects and dubbing — everything a product video or ad needs on the audio side.",
    bestFor: ["Ad & video voiceovers", "Founder voice clone", "Sound effects", "Dubbing into other languages"],
    tip: "Paste the voiceover script from Video AI, pick a voice that matches your brand personality, and export as MP3 for CapCut.",
  },
  {
    id: "suno",
    name: "Suno",
    group: "audio",
    domain: "suno.com",
    url: "https://suno.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Original music beds and jingles for videos and ads.",
    why: "Generates full songs or instrumentals from a description, so you can have an on-brand music bed without licensing headaches.",
    bestFor: ["Background music", "Jingles", "Sonic branding experiments"],
    tip: "Ask for “instrumental, 30 seconds, upbeat, no vocals” to get a clean ad bed.",
  },
  {
    id: "descript",
    name: "Descript",
    group: "audio",
    domain: "descript.com",
    url: "https://www.descript.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Edit video and audio by editing the transcript.",
    why: "Removes filler words, fixes audio, and lets you cut founder videos or podcast clips like a text document.",
    bestFor: ["Founder talking-head edits", "Podcast & interview clips", "Cleaning up audio"],
    tip: "Use Studio Sound to make a phone recording sound like a studio mic.",
  },

  // ── DESIGN PRODUCTION ──────────────────────────────────────────────────────
  {
    id: "illustrator",
    name: "Adobe Illustrator",
    group: "production",
    domain: "adobe.com",
    url: "https://www.adobe.com/products/illustrator.html",
    recommended: true,
    pricing: "Paid",
    tagline: "Where AI logo and packaging concepts become print-ready vector files.",
    why: "The industry standard printers and co-packers expect. Image Trace converts AI logo concepts to vector, and dielines, Pantone colors and outlines all live here.",
    bestFor: ["Vectorizing AI logos", "Packaging dielines", "Print-ready artwork", "Pantone matching"],
    tip: "Image Trace → Expand → clean up anchor points, then rebuild the type with a real font so it stays editable.",
  },
  {
    id: "photoshop",
    name: "Adobe Photoshop",
    group: "production",
    domain: "adobe.com",
    url: "https://www.adobe.com/products/photoshop.html",
    recommended: false,
    pricing: "Paid",
    tagline: "Retouch AI renders and composite products into real photos.",
    why: "Generative Fill, background extension, color correction and compositing make AI product shots retailer-ready.",
    bestFor: ["Retouching renders", "Compositing", "Background cleanup", "Amazon white-background images"],
    tip: "Use Select Subject → Generative Fill to swap backgrounds while keeping your real packaging pixel-perfect.",
  },
  {
    id: "figma",
    name: "Figma",
    group: "production",
    domain: "figma.com",
    url: "https://www.figma.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Collaborative design for websites, decks and brand systems.",
    why: "Build your brand system, website mockups and presentation layouts in one shared space; free for small teams and great for handing off to developers.",
    bestFor: ["Website mockups", "Brand guidelines", "Collaborating with designers"],
    tip: "Paste your AI-generated sitemap and homepage copy into a Figma wireframe before building in Wix or Shopify.",
  },
  {
    id: "vectorizer",
    name: "Vectorizer.AI",
    group: "production",
    domain: "vectorizer.ai",
    url: "https://vectorizer.ai/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "One-click conversion of AI logo images into clean SVG.",
    why: "Better-than-Illustrator auto-tracing for logos and icons; the fastest bridge between an image tool and a usable vector.",
    bestFor: ["Logo PNG → SVG", "Icon conversion"],
    tip: "Generate the logo at the highest resolution possible on a plain white background before uploading.",
  },
  {
    id: "removebg",
    name: "remove.bg",
    group: "production",
    domain: "remove.bg",
    url: "https://www.remove.bg/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Instant background removal for product photos.",
    why: "Cuts out your product in seconds so you can drop it into AI-generated lifestyle scenes or Amazon-compliant white backgrounds.",
    bestFor: ["Product cut-outs", "Amazon main images", "Prepping photos for AI scenes"],
    tip: "Cut out the real product, then ask ChatGPT to place it in a new scene for photoreal lifestyle shots.",
  },

  // ── WEBSITES & E-COMMERCE ──────────────────────────────────────────────────
  {
    id: "shopify",
    name: "Shopify",
    group: "website",
    domain: "shopify.com",
    url: "https://www.shopify.com/",
    recommended: true,
    pricing: "Paid",
    tagline: "The default DTC storefront for CPG brands.",
    why: "Built for selling physical products — subscriptions, bundles, wholesale channels, retail POS and every CPG app you will eventually need. Shopify Magic writes product descriptions in-platform.",
    bestFor: ["DTC store", "Subscriptions & bundles", "Wholesale portal", "Scaling brands"],
    tip: "Paste the Website AI product-page copy into each product, then use Shopify Magic to generate variants.",
  },
  {
    id: "wix",
    name: "Wix",
    group: "website",
    domain: "wix.com",
    url: "https://www.wix.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Fastest brand site with AI site generation built in.",
    why: "Wix's AI builder creates a full site from a description, and the editor is easy for founders who want to manage it themselves. Good for brand-first sites with light e-commerce.",
    bestFor: ["Brand & story sites", "Pre-launch pages", "Light e-commerce", "Founder-managed sites"],
    tip: "Feed the Wix AI builder your Website AI sitemap and homepage copy to skip the blank page.",
  },
  {
    id: "lovable",
    name: "Lovable",
    group: "website",
    domain: "lovable.dev",
    url: "https://lovable.dev/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Describe a custom landing page or web app and get working code.",
    why: "For custom experiences (quizzes, calculators, waitlists, interactive landing pages) that templates can’t do — no developer required.",
    bestFor: ["Custom landing pages", "Waitlists & quizzes", "Interactive experiences"],
    tip: "Paste the Landing Page prompt output as your build brief; ask for mobile-first and a single clear CTA.",
  },
  {
    id: "framer",
    name: "Framer",
    group: "website",
    domain: "framer.com",
    url: "https://www.framer.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Design-forward marketing sites with polished animation.",
    why: "The choice for premium-feeling brand sites with motion and strong typography; AI helps generate sections and copy.",
    bestFor: ["Premium brand sites", "Animated landing pages", "Design-led brands"],
  },

  // ── PRESENTATIONS & SELL SHEETS ────────────────────────────────────────────
  {
    id: "gamma",
    name: "Gamma",
    group: "presentation",
    domain: "gamma.app",
    url: "https://gamma.app/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "Paste AI-written deck content and get a designed presentation.",
    why: "Turns the outline from Retail AI or Investor AI into a designed deck, one-pager or web page in minutes, with AI images and easy restyling.",
    bestFor: ["Buyer presentations", "Investor decks", "Sell sheets", "Distributor one-pagers"],
    tip: "Paste the full deck outline in “Paste in text” mode so each slide’s content stays exactly as written.",
  },
  {
    id: "pitch",
    name: "Pitch",
    group: "presentation",
    domain: "pitch.com",
    url: "https://pitch.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Collaborative, beautiful decks with analytics on who viewed them.",
    why: "Clean templates, team collaboration and link-sharing with view analytics — useful for knowing which buyer or investor actually opened your deck.",
    bestFor: ["Investor decks", "Shared buyer decks", "View tracking"],
  },
  {
    id: "canva-present",
    name: "Canva Presentations",
    group: "presentation",
    domain: "canva.com",
    url: "https://www.canva.com/presentations/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Brand-kit-driven decks and sell sheets for non-designers.",
    why: "Uses your Brand Kit automatically and exports print-ready PDFs for sell sheets and line cards.",
    bestFor: ["Sell sheets", "Line cards", "Quick decks"],
  },

  // ── RESEARCH & DATA ────────────────────────────────────────────────────────
  {
    id: "perplexity-research",
    name: "Perplexity Deep Research",
    group: "research",
    domain: "perplexity.ai",
    url: "https://www.perplexity.ai/",
    recommended: true,
    pricing: "Free + paid",
    tagline: "Sourced market and competitor reports in minutes.",
    why: "Runs dozens of searches, reads the results and writes a cited report — the right tool for every Research AI prompt where facts must be current.",
    bestFor: ["Market analysis", "Competitor deep-dives", "Retailer research", "Trend reports"],
  },
  {
    id: "gemini-research",
    name: "Gemini Deep Research",
    group: "research",
    domain: "gemini.google.com",
    url: "https://gemini.google.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Long, structured research reports exported straight to Google Docs.",
    why: "Plans a research approach, browses widely and produces a multi-page report you can export to Docs and share with your team.",
    bestFor: ["Category landscape reports", "Consumer insight summaries"],
  },
  {
    id: "google-sheets",
    name: "Google Sheets (+ Gemini)",
    group: "research",
    domain: "sheets.google.com",
    url: "https://sheets.google.com/",
    recommended: false,
    pricing: "Free + paid",
    tagline: "Where Finance AI outputs become a working model.",
    why: "Paste the margin waterfall, COGS comparison or scenario tables from Finance AI and keep them live; Gemini in Sheets can build formulas from plain English.",
    bestFor: ["Margin & pricing models", "COGS tracking", "Scenario planning"],
    tip: "Ask Finance AI prompts to “output as a table I can paste into Google Sheets.”",
  },
];

export const TOOL_BY_ID = Object.fromEntries(AI_TOOLS.map((t) => [t.id, t]));

export function toolsForGroup(groupId) {
  return AI_TOOLS.filter((t) => t.group === groupId).sort((a, b) => Number(b.recommended) - Number(a.recommended));
}

export function toolLogoUrl(tool) {
  return `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
}
